import { Channel } from 'lib-shared-types-channels';

import { searchApi } from 'apis/search';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';

import { channelAttributes } from './channels';

type UseAvailableChannelsOptions = {
  /**
   * When true, skips the Search API usage check and returns all channels that
   * pass feature flag checks. Useful for selectors where the organization may
   * not have conversations yet (e.g. SLA, automations onboarding).
   */
  readonly skipUsageCheck?: boolean;
};

type UseAvailableChannelsResult = {
  /** The available channels enriched with attributes. */
  readonly availableChannels: ReadonlyArray<
    {
      /** The channel key. */
      readonly channel: Channel;
    } & (typeof channelAttributes)[Channel]
  >;
};

/**
 * A hook that fetches and filters the available channels for the current
 * organization. Channels are eagerly fetched from the Search API on first
 * invocation and filtered by feature flags and known channel attributes.
 *
 * Pass `{ skipUsageCheck: true }` to return all feature-flagged channels
 * without requiring them to exist in the Search API.
 */
export const useAvailableChannels = (
  options?: UseAvailableChannelsOptions,
): UseAvailableChannelsResult => {
  const channelFeatureFlags = getChannelFeatureFlagsStatus();

  const { availableChannels } = searchApi.useReadSearchChannelsQuery(
    { scope: 'channels' },
    {
      skip: options?.skipUsageCheck,
      selectFromResult: ({ data }) => {
        const apiChannels = new Set(
          data?.data.results.map(({ result }) => result) ?? [],
        );

        return {
          availableChannels: (Object.keys(channelAttributes) as Channel[])
            .filter(
              (channel) =>
                channelFeatureFlags.get(channel) &&
                (apiChannels.size === 0 || apiChannels.has(channel)),
            )
            .map((channel) => ({
              channel,
              ...channelAttributes[channel],
            })),
        };
      },
    },
  );

  return { availableChannels };
};
